import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import moment from 'moment';

// HOCs
import withLocalization from '../../../../hoc/withLocalization';
import applicationRouter from '../../../../hoc/applicationRouter';

//Components
import AssignPattern from './AssignPattern';
import ManageAssignments from './ManageAssignments';
import LoadingSpinner from '~/elements/LoadingSpinner';
import Button from '../../../../components/CustomButton/CustomButton.jsx';

//elements
import { DATE_FORMAT } from '../RosterScheduler/index.js';

//utils
import { DEFAULT_VISIBILITY_WEEKS } from '../../../../utils/constants';
import { validateModuleAccess } from '../../../../library/stores/userStore';

//Styles
import '../RosterScheduler/css/workPatternStyle.css';

class WorkPatternAutomation extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: true,
            activeTab: 'assign',
            departmentOptions: [],
            selectedDepartment: null,
            selectedWorkPattern: null,
            workPatternDetails: null,
            allResources: [],
            filteredEmployees: [],
            assignedUserIds: [],
            user_group: null,
            assignmentUserIds: [],
            assignmentStartDate: moment().add(DEFAULT_VISIBILITY_WEEKS * 7, 'days').format(DATE_FORMAT),
            assignmentEndDate: '',
            overrideComment: '',
            isLoadingDetails: false,
            nextTemplateId: null,
            cycleWeekLength: null,
            visibilityWeeks: DEFAULT_VISIBILITY_WEEKS,
            autoTimesheetDays: { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },
            overrideShift: false,
            overrideAutoTimeRecording: false,
        };
    }

    componentDidMount() {
        const activeTab = this.props?.router?.location?.state?.activeTab;
        if (activeTab) {
            this.setState({ activeTab });
        } else {
                const canManage = validateModuleAccess('md_WorkPattern', 'manageWorkPatterns');
            if (!canManage) this.setState({ activeTab: 'assignments' });
        }
        this.loadResources();
    }

    loadResources = async () => {
        const { commonStore, scheduleStore, t, userStore } = this.props;
        try {
            const departments = commonStore?.config?.client?.data?.roles || [];
            const departmentOptions = departments.map(d => ({ label: d.name, value: d.code }));
            departmentOptions.push({ label: t('Others'), value: 'z_none' });
            let pmMembers = '';
            if (userStore.currentUser?.user_type !== 'admin' && userStore.currentUser?.Manages?.length) {
                pmMembers = userStore.currentUser.Manages.join(',');
            }
            const resourceResponse = await scheduleStore.fetchResourcesOnly({ pmMembers });
            const allResources = (resourceResponse.resources || []).slice(1);
            const firstDept = departmentOptions[0]?.value || null;
            this.setState({
                departmentOptions,
                selectedDepartment: firstDept,
                allResources,
                isLoading: false,
            }, () => this.handleDepartmentChange(firstDept));
        } catch (error) {
            console.error('Error loading resources:', error);
            commonStore.addNotification(t('Failed to load work patterns'), null, 'error');
            this.setState({ isLoading: false });
        }
    }

    getWorkPatternLoadFn = (name, page) => {
        const { rosterStore } = this.props;
        const { selectedDepartment } = this.state;
        return rosterStore.getWorkPatterns({ name, page, pagesize: 10, department: selectedDepartment || '' });
    }

    handleInputChange = (key, value) => {
        const val = value?.target ? value.target.value : value;
        this.setState({
            [key]: val,
            ...(key === 'assignmentEndDate' && !val && { nextTemplateId: null })
        }, () => {
            if (key === 'selectedDepartment') this.handleDepartmentChange(val);
            if (key === 'user_group') this.handleGroupChange(val);
        });
    }

    handleWorkPatternSelect = async (option) => {
        if (!option) {
            this.setState({ selectedWorkPattern: null, workPatternDetails: null, assignedUserIds: [] });
            return;
        }

        this.setState({ selectedWorkPattern: option.value, workPatternDetails: null, isLoadingDetails: true });

        try {
            const details = await this.props.rosterStore.getWorkPatternSelectionDetails({ template_id: option.value });

            this.setState({
                workPatternDetails: { ...option, ...details },
                assignedUserIds: details.assignedUserIds || [],
                isLoadingDetails: false
            });
        } catch (error) {
            console.error("Failed to fetch pattern details:", error);
            this.setState({ isLoadingDetails: false });
        }
    }

    handleDepartmentChange = async (value) => {
        const { allResources } = this.state;
        const { clientStore } = this.props;
        const filteredEmployees = value ? allResources.filter(r => r.employee_type?.toLowerCase() === value.toLowerCase()) : allResources;
        this.setState({
            selectedDepartment: value,
            filteredEmployees,
            assignedUserIds: [],
            user_group: null,
            selectedWorkPattern: null,
            workPatternDetails: null,
            assignmentUserIds: [],
            cycleWeekLength: null,
            nextTemplateId: null,
            autoTimesheetDays: { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },
            overrideShift: false,
            overrideAutoTimeRecording: false,
        });
        if (value) {
            try {
                const workHoursRule = await clientStore.getWorkHoursRuleByDepartment(value);
                const visibilityWeeks = workHoursRule?.work_pattern_visibility_weeks || DEFAULT_VISIBILITY_WEEKS;
                this.setState({ cycleWeekLength: workHoursRule?.cycle_week_length || null, visibilityWeeks, assignmentStartDate: moment().add(visibilityWeeks * 7, 'days').format(DATE_FORMAT) });
            } catch {
                this.setState({ cycleWeekLength: null, visibilityWeeks: DEFAULT_VISIBILITY_WEEKS, assignmentStartDate: moment().add(DEFAULT_VISIBILITY_WEEKS * 7, 'days').format(DATE_FORMAT) });
            }
        }
    }

    handleAssignSuccess = () => {
        const { rosterStore } = this.props;
        const { selectedDepartment } = this.state;
        this.setState({
            activeTab: 'assignments',
            selectedWorkPattern: null,
            workPatternDetails: null,
            assignmentUserIds: [],
            assignmentStartDate: moment().add((this.state.visibilityWeeks || DEFAULT_VISIBILITY_WEEKS) * 7, 'days').format(DATE_FORMAT),
            assignmentEndDate: '',
            overrideComment: '',
            user_group: null,
            nextTemplateId: null,
            autoTimesheetDays: { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },
            overrideShift: false,
            overrideAutoTimeRecording: false,
        }, () => {
            if (selectedDepartment) this.handleDepartmentChange(selectedDepartment);
            rosterStore.refreshAssignmentList();
        });
    }

    handleGroupChange = async (value) => {
        const { userGroupStore } = this.props;
        const { allResources, selectedDepartment } = this.state;
        this.setState({ assignmentUserIds: [] });
        if (value > 0) {
            const { user_groups } = await userGroupStore.getUsersInGroup(value);
            const groupIds = new Set(user_groups.map(r => r.user_id));
            const filteredEmployees = allResources.filter(r => groupIds.has(r.id) && (!selectedDepartment || r.employee_type === selectedDepartment));
            this.setState({ filteredEmployees });
        } else {
            const filteredEmployees = selectedDepartment ? allResources.filter(r => r.employee_type === selectedDepartment) : allResources;
            this.setState({ filteredEmployees });
        }
    }

    render() {
        const { isLoading, activeTab } = this.state;
        const { t, router } = this.props;
        if (isLoading) return <LoadingSpinner />;

        const canManageWorkPattern = validateModuleAccess('md_WorkPattern', 'manageWorkPatterns');
        const canViewWorkPattern = validateModuleAccess('md_WorkPattern', 'view');

        return (
            <div className='px-3'>
                <div className="d-flex align-items-center justify-content-between py-3 position-sticky top-0 bg-white border-bottom" style={{ zIndex: 10 }}>
                    <Button
                        onClick={() => this.props.router.navigate(-1)}
                    >
                        <i className="fas fa-arrow-left me-2"></i>
                        {t('Back')}
                    </Button>
                    <div className="d-flex align-items-center gap-3">
                        {activeTab === 'assignments' && (
                            <span className="fs12 d-flex align-items-center gap-1">
                                <i className="fas fa-circle fa-xs text-primary-color fw-bold" />
                                {t('Includes multiple work patterns')}
                            </span>
                        )}
                        {canManageWorkPattern && (
                            <Button
                                fill
                                me2
                                onClick={() => router.navigate('/admin/roster/work-pattern')}
                            >
                                <i className="fa fa-plus" />
                                {`${t('Create')} ${t('Work Pattern')}`}
                            </Button>
                        )}

                        <div className='d-flex rounded-2 overflow-hidden'>
                            {canManageWorkPattern && (
                                <button className={`tab-btn tab-btn-left${activeTab === 'assign' ? ' active' : ''}`} onClick={() => this.setState({ activeTab: 'assign' })}>
                                    {t('Assign')} {t('New')}
                                </button>
                            )}
                            {canViewWorkPattern && (
                                <button className={`tab-btn tab-btn-right${activeTab === 'assignments' ? ' active' : ''}`} onClick={() => this.setState({ activeTab: 'assignments' })}>
                                    {t('Assignments')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {activeTab === 'assign' ? (
                    <AssignPattern
                        parentState={this.state}
                        t={t}
                        router={this.props.router}
                        handleClose={() => this.props.router.navigate(-1)}
                        handleInputChange={this.handleInputChange}
                        handleWorkPatternSelect={this.handleWorkPatternSelect}
                        onAssignSuccess={this.handleAssignSuccess}
                        workPatternLoadFn={this.getWorkPatternLoadFn}
                    />
                ) : (
                    <ManageAssignments
                        t={t}
                        router={this.props.router}
                    />
                )}
            </div>
        );
    }
}

export default inject('rosterStore', 'commonStore', 'scheduleStore', 'userGroupStore', 'userStore', 'clientStore')(
    withLocalization(applicationRouter(observer(WorkPatternAutomation)))
);
